import React, { useState } from 'react';

import { Box, Button, Divider, Fade, Paper, Typography, useTheme } from '@material-ui/core';
import { RiEyeLine } from 'react-icons/ri';

import { getUKFormattedDate } from '@vyce/core/src/utils/dates';
import { FormRecipientsDialogType, User } from '@vyce/core/src/types';

import { FormattedDescription } from '@vyce/core/src/components/FormattedDescription';
import { FormRecipientsDialog } from './FormRecipientsDialog';
export interface FormSummaryTitleProps {
  title: string;
  sent?: number;
  completed?: number;
  description?: string;
  createdByName?: string;
  status: string;
  createdAt?: { _seconds: number } | Date | null;
  companyId: string;
  formId: string;
  user: User;
}

export const FormSummaryTitle: React.FC<FormSummaryTitleProps> = ({
  title,
  sent,
  completed,
  description,
  createdByName,
  status,
  createdAt,
  companyId,
  formId,
  user,
}) => {
  const theme = useTheme();
  const createdAtMs = createdAt
    ? createdAt instanceof Date
      ? createdAt.getTime()
      : createdAt._seconds * 1000
    : null;
  const [openRecipientsDialog, setOpenRecipientsDialog] = useState(false);
  const [dialogType, setDialogType] = useState<FormRecipientsDialogType>('recipients');
  const showStats = sent !== undefined && completed !== undefined;

  return (
    <Fade in={true} timeout={200} style={{ width: '100%' }}>
      <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
        <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
          {title}
        </Typography>

        {!!description && (
          <Box marginBottom={1}>
            <FormattedDescription text={description} variant="body1" />
          </Box>
        )}

        {!!createdByName && (
          <Box display="flex" alignItems="center" marginBottom={2}>
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Created by:
            </Typography>
            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {createdByName}
            </Typography>
          </Box>
        )}

        {!!status && (
          <Box display="flex" alignItems="center" marginBottom={2}>
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Status:
            </Typography>
            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Typography>
          </Box>
        )}

        {!!createdAt && (
          <Box display="flex" alignItems="center" marginBottom={showStats ? 2 : 0}>
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Date created:
            </Typography>
            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {createdAtMs ? getUKFormattedDate(createdAtMs) : ''}
            </Typography>
          </Box>
        )}

        {showStats && <Divider />}

        {showStats && (
          <Box display="flex" alignItems="center" marginTop={2} marginBottom={1}>
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Sent to:
            </Typography>
            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {sent}
            </Typography>

            <Box flex={1} />

            <Button
              className="no-print"
              style={{ borderColor: theme.palette.divider, height: 32 }}
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => {
                setDialogType('recipients');
                setOpenRecipientsDialog(true);
              }}
              startIcon={<RiEyeLine size={16} />}
            >
              View recipients
            </Button>
          </Box>
        )}

        {showStats && (
          <Box display="flex" alignItems="center" marginBottom={1}>
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Responses:
            </Typography>
            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {completed}
            </Typography>

            <Box flex={1} />

            <Button
              className="no-print"
              style={{ borderColor: theme.palette.divider, height: 32 }}
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<RiEyeLine size={16} />}
              onClick={() => {
                setDialogType('responses');
                setOpenRecipientsDialog(true);
              }}
            >
              View responses
            </Button>
          </Box>
        )}

        {showStats && (
          <Box display="flex" alignItems="center">
            <Typography style={{ fontWeight: 500 }} variant="body1" color="textSecondary">
              Outstanding:
            </Typography>

            <Typography style={{ marginLeft: 6 }} variant="body1" color="textSecondary">
              {sent - completed}
            </Typography>

            <Box flex={1} />

            <Button
              className="no-print"
              style={{ borderColor: theme.palette.divider, height: 32 }}
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => {
                setDialogType('outstanding');
                setOpenRecipientsDialog(true);
              }}
              startIcon={<RiEyeLine size={16} />}
            >
              View outstanding
            </Button>
          </Box>
        )}

        <FormRecipientsDialog
          open={openRecipientsDialog}
          onClose={() => {
            setOpenRecipientsDialog(false);
          }}
          type={dialogType}
          companyId={companyId}
          formId={formId}
          user={user}
        />
      </Paper>
    </Fade>
  );
};
